import { IdAsUUID } from "./commonTypes";
import * as yup from "yup";

export const YupTypedSelectOption = yup.object({
  label: yup.string().defined().required(),
  type: yup.string().defined().required(),
  group: yup.string().nullable(),
  value: yup
    .object({
      id: yup.mixed().defined().required(),
    })
    .defined()
    .required(),
});

type SelectOptionValue<
  T extends Record<string, any> = Record<string, any>,
  I = undefined,
> = {
  id: I extends undefined ? string : I;
} & T;

export interface SelectOptionRegistry {}

export interface TypedSelectOption<
  TType extends string = "",
  TValue extends Record<string, any> = Record<string, any>,
  TI = undefined,
> {
  label: string;
  group?: string;
  type: TType;
  value: SelectOptionValue<TValue, TI>;
}

export function hasGroupTyped<
  TType extends string = "",
  TValue extends Record<string, any> = Record<string, any>,
  TI = undefined,
>(
  option?: TypedSelectOption<TType, TValue, TI>,
): option is TypedSelectOption<TType, TValue, TI> & { group: string } {
  return !!option && typeof option.group === "string";
}

export type ItemOption<
  TValue extends Record<string, any> = Record<string, any>,
> = TypedSelectOption<"item", TValue, IdAsUUID>;
export type UnitOption<
  TValue extends Record<string, any> = Record<string, any>,
> = TypedSelectOption<"unit", TValue, IdAsUUID>;
export type UnitTypeOption<
  TValue extends Record<string, any> = Record<string, any>,
> = TypedSelectOption<"unittype", TValue, IdAsUUID>;
export type LotOption<
  TValue extends Record<string, any> = Record<string, any>,
> = TypedSelectOption<"lot", TValue, IdAsUUID>;

export type POOption<TValue extends Record<string, any> = Record<string, any>> =
  TypedSelectOption<"purchaseorder", TValue, IdAsUUID>;
export type SOOption<TValue extends Record<string, any> = Record<string, any>> =
  TypedSelectOption<"salesorder", TValue, IdAsUUID>;
export type WOOption<TValue extends Record<string, any> = Record<string, any>> =
  TypedSelectOption<"workorder", TValue, IdAsUUID>;
export type BomRevOption<
  TValue extends Record<string, any> = Record<string, any>,
> = TypedSelectOption<"bomrev", TValue, IdAsUUID>;

export type LocationOption<
  TValue extends Record<string, any> = Record<string, any>,
> = TypedSelectOption<"location", TValue, IdAsUUID>;
export type CountryOption<
  TValue extends Record<string, any> = Record<string, any>,
> = TypedSelectOption<"worldcountry", TValue, number>;
export type StateOption<
  TValue extends Record<string, any> = Record<string, any>,
> = TypedSelectOption<"worldstate", TValue, number>;
export type CityOption<
  TValue extends Record<string, any> = Record<string, any>,
> = TypedSelectOption<"worldcity", TValue, number>;

export type AddressOption<
  TValue extends Record<string, any> = Record<string, any>,
> = TypedSelectOption<"address", TValue, IdAsUUID>;

export type EntityOption<
  TValue extends Record<string, any> = Record<string, any>,
> = TypedSelectOption<"genericentity", TValue, IdAsUUID>;
export type EmployeeOption<
  TValue extends Record<string, any> = Record<string, any>,
> = TypedSelectOption<"employee", TValue, IdAsUUID>;
export type VendorOption<
  TValue extends Record<string, any> = Record<string, any>,
> = TypedSelectOption<"vendor", TValue, IdAsUUID>;
export type CustomerOption<
  TValue extends Record<string, any> = Record<string, any>,
> = TypedSelectOption<"customer", TValue, IdAsUUID>;
export type ContactOption<
  TValue extends Record<string, any> = Record<string, any>,
> = TypedSelectOption<"contact", TValue, IdAsUUID>;

export type InventoryStatusOption<
  TValue extends Record<string, any> = Record<string, any>,
> = TypedSelectOption<"invstatus", TValue, IdAsUUID>;
export type InventoryPositionOption<
  TValue extends Record<string, any> = Record<string, any>,
> = TypedSelectOption<"invposition", TValue, IdAsUUID>;
